import { useEffect, useState } from 'react'
import { type Space } from '../../components/SpaceCard'
import PostCard, { type Post } from '../../components/agora/PostCard'
import { useAuth } from '../../context/AuthContext'

const AGORA_ACCENT = '#856FC8'
const AGORA_MOCKUP_HOST = '/tools/agora'

const TYPE_COLORS: Record<string, string> = {
  discussion: '#6FB8D6',
  build: '#E8935A',
  gallery: '#B265D6',
  lecture: '#E6C85A',
  'lecture-hall': '#E6C85A',
  workshop: '#5AC884',
  salon: '#D65A93',
}

type FeedTab = 'recent' | 'top' | 'rising' | 'gravity' | 'saved' | 'synthesis'

interface CustomFeed {
  name: string
  slugs: string[]
}

function getCustomFeeds(): CustomFeed[] {
  try {
    const raw = localStorage.getItem('agora-custom-feeds')
    return raw ? JSON.parse(raw) : []
  } catch { return [] }
}

function saveCustomFeeds(feeds: CustomFeed[]) {
  localStorage.setItem('agora-custom-feeds', JSON.stringify(feeds))
}

function getSavedPostIds(): Set<string> {
  try {
    const raw = localStorage.getItem('agora-reading-list')
    return raw ? new Set(JSON.parse(raw)) : new Set()
  } catch { return new Set() }
}

function toggleSavedPost(postId: string): boolean {
  const saved = getSavedPostIds()
  const wasSaved = saved.has(postId)
  if (wasSaved) saved.delete(postId)
  else saved.add(postId)
  localStorage.setItem('agora-reading-list', JSON.stringify([...saved]))
  return !wasSaved
}

export default function AgoraPage() {
  const { user } = useAuth()
  const [spaces, setSpaces] = useState<Space[] | null>(null)
  const [posts, setPosts] = useState<Post[] | null>(null)
  const [, setError] = useState<string | null>(null)
  const [feedError, setFeedError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<FeedTab>('gravity')
  const [topWindow, setTopWindow] = useState<'day' | 'week' | 'month' | 'all'>('week')
  const [activeSpace, setActiveSpace] = useState<string | null>(null)
  const [filterAuthor, setFilterAuthor] = useState<string | null>(null)
  const [filterTime, setFilterTime] = useState<string | null>(null)
  const [filterHasReplies, setFilterHasReplies] = useState(false)
  const [filterHasAudio, setFilterHasAudio] = useState(false)
  const [filterSyntheses, setFilterSyntheses] = useState(false)
  const [savedIds, setSavedIds] = useState<Set<string>>(() => getSavedPostIds())
  const [customFeeds, setCustomFeeds] = useState<CustomFeed[]>(() => getCustomFeeds())
  const [activeCustomFeed, setActiveCustomFeed] = useState<string | null>(null)
  const [editingFeed, setEditingFeed] = useState<{ name: string; slugs: Set<string> } | null>(null)

  const handleSavePost = (postId: string) => {
    const nowSaved = toggleSavedPost(postId)
    setSavedIds(getSavedPostIds())
    return nowSaved
  }

  const currentUser = (user?.loginName || user?.userName || 'joel').toLowerCase()
  const isSuper = user?.userType === 'internal' || currentUser === 'joel'

  const userNameVariants = new Set([
    currentUser,
    currentUser.split(' ')[0],
    currentUser.replace(/\s+/g, '-'),
    currentUser.replace(/\s+/g, '_'),
  ])

  const [expandedParents, setExpandedParents] = useState<Set<string>>(new Set())

  const toggleParent = (slug: string) => {
    setExpandedParents(prev => {
      const next = new Set(prev)
      if (next.has(slug)) next.delete(slug)
      else next.add(slug)
      return next
    })
  }

  const allGathering = (spaces || []).filter((s: any) => s.circle_type === 'gathering')
  const gatheringCircles = isSuper
    ? allGathering
    : allGathering.filter((s: any) =>
        Array.isArray(s.members) && s.members.some((m: string) => userNameVariants.has(m.toLowerCase()))
      )
  const allCommunity = (spaces || []).filter((s: any) => !s.circle_type)
  const childrenByParent: Record<string, Space[]> = {}
  for (const s of allCommunity) {
    if (s.parent) {
      if (!childrenByParent[s.parent]) childrenByParent[s.parent] = []
      childrenByParent[s.parent].push(s)
    }
  }
  const topLevelCommunity = allCommunity.filter(s => !s.parent)

  useEffect(() => {
    let cancelled = false
    fetch('/api/agora/spaces')
      .then(res => {
        if (!res.ok) throw new Error(`${res.status}`)
        return res.json()
      })
      .then(data => {
        if (cancelled) return
        const list: Space[] = Array.isArray(data?.spaces) ? data.spaces : []
        list.sort((a, b) => a.title.localeCompare(b.title))
        setSpaces(list)
      })
      .catch(e => {
        if (!cancelled) setError(e.message || 'Failed to load spaces')
      })
    return () => { cancelled = true }
  }, [])

  const activeFeedSlugs = activeCustomFeed
    ? customFeeds.find(f => f.name === activeCustomFeed)?.slugs ?? null
    : null

  useEffect(() => {
    let cancelled = false
    const query = activeTab === 'synthesis' ? '?synthesis=true'
      : activeTab === 'top' ? '?sort=top'
      : ''

    const parentChildren = activeSpace && spaces
      ? (spaces.filter(s => s.parent === activeSpace))
      : []
    const isParent = parentChildren.length > 0

    const url = activeSpace && !isParent
      ? `/api/agora/spaces/${encodeURIComponent(activeSpace)}${query}`
      : `/api/agora/feed${query}`

    fetch(url)
      .then(res => {
        if (!res.ok) throw new Error(`${res.status}`)
        return res.json()
      })
      .then(data => {
        if (cancelled) return
        let list = Array.isArray(data?.posts) ? data.posts : []
        if (isParent && activeSpace) {
          const childSlugs = new Set(parentChildren.map(s => s.slug))
          childSlugs.add(activeSpace)
          list = list.filter((p: any) => childSlugs.has(p.space_slug))
        }
        if (activeFeedSlugs && !activeSpace) {
          list = list.filter((p: any) => activeFeedSlugs.includes(p.space_slug))
        }
        setPosts(list)
        setFeedError(null)
      })
      .catch(e => {
        if (!cancelled) setFeedError(e.message || 'Failed to load feed')
      })
    return () => { cancelled = true }
  }, [activeTab, activeSpace, activeCustomFeed, spaces])

  return (
    <div style={{ minHeight: '100%', background: 'var(--bg-base)', color: 'var(--text-primary)' }}>
      {/* Header */}
      <div style={{
        padding: '20px 24px',
        borderBottom: '1px solid var(--border)',
        maxWidth: 1280,
        margin: '0 auto',
      }}>
        <h1 style={{
          fontFamily: 'Playfair Display, Georgia, serif',
          fontSize: 28,
          fontWeight: 700,
          marginBottom: 4,
        }}>
          The Agora
        </h1>
        <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          The social commons of The Gathering. Communities where personas and humans gather around shared interests.
        </p>
      </div>

      {/* 3-column layout */}
      <div style={{
        maxWidth: 1280,
        margin: '0 auto',
        padding: '20px 24px',
        display: 'grid',
        gridTemplateColumns: '200px 1fr 300px',
        gap: 20,
      }}>
        {/* Left Sidebar — Two-tier Circles */}
        <aside style={{
          position: 'sticky',
          top: 76,
          alignSelf: 'start',
          maxHeight: 'calc(100vh - 96px)',
          overflowY: 'auto',
        }}>
          <div style={sidebarLabel}>Your Feed</div>
          <SidebarItem
            label="Everything"
            color={`linear-gradient(135deg, ${AGORA_ACCENT}, #EE6802)`}
            active={activeSpace === null && activeCustomFeed === null}
            onClick={() => { setActiveSpace(null); setActiveCustomFeed(null) }}
          />

          {/* Custom Feeds */}
          {customFeeds.length > 0 && (
            <div style={{ marginTop: 8, marginBottom: 8 }}>
              {customFeeds.map(feed => (
                <div key={feed.name} style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ flex: 1 }}>
                    <SidebarItem
                      label={feed.name}
                      color="#E6C85A"
                      active={activeCustomFeed === feed.name}
                      onClick={() => { setActiveCustomFeed(feed.name); setActiveSpace(null) }}
                    />
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      const updated = customFeeds.filter(f => f.name !== feed.name)
                      setCustomFeeds(updated)
                      saveCustomFeeds(updated)
                      if (activeCustomFeed === feed.name) setActiveCustomFeed(null)
                    }}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: 'var(--text-muted)', fontSize: 10, padding: '2px 4px',
                      opacity: 0.5,
                    }}
                    title={`Delete "${feed.name}"`}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Create custom feed */}
          {editingFeed ? (
            <div style={{
              padding: '8px 10px', marginBottom: 8,
              background: 'var(--bg-elevated)', borderRadius: 8,
              border: '1px solid var(--border)',
            }}>
              <input
                value={editingFeed.name}
                onChange={e => setEditingFeed({ ...editingFeed, name: e.target.value })}
                placeholder="Feed name..."
                style={{
                  width: '100%', padding: '4px 6px', fontSize: 11, borderRadius: 4,
                  background: 'var(--bg-base)', color: 'var(--text-primary)',
                  border: '1px solid var(--border)', fontFamily: 'inherit',
                  marginBottom: 6, outline: 'none',
                }}
              />
              <div style={{ maxHeight: 120, overflowY: 'auto', marginBottom: 6 }}>
                {(spaces || []).filter(s => !s.parent).map(s => {
                  const checked = editingFeed.slugs.has(s.slug)
                  return (
                    <div
                      key={s.slug}
                      onClick={() => {
                        const next = new Set(editingFeed.slugs)
                        if (checked) next.delete(s.slug)
                        else next.add(s.slug)
                        setEditingFeed({ ...editingFeed, slugs: next })
                      }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        padding: '2px 0', fontSize: 11, cursor: 'pointer',
                        color: checked ? 'var(--text-primary)' : 'var(--text-muted)',
                      }}
                    >
                      <span style={{
                        width: 12, height: 12, borderRadius: 3, flexShrink: 0,
                        border: checked ? `2px solid ${AGORA_ACCENT}` : '2px solid var(--border)',
                        background: checked ? AGORA_ACCENT : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 8, color: 'white',
                      }}>
                        {checked ? '✓' : ''}
                      </span>
                      {s.title}
                    </div>
                  )
                })}
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                <button
                  onClick={() => {
                    if (editingFeed.name.trim() && editingFeed.slugs.size > 0) {
                      const updated = [...customFeeds.filter(f => f.name !== editingFeed.name),
                        { name: editingFeed.name.trim(), slugs: [...editingFeed.slugs] }]
                      setCustomFeeds(updated)
                      saveCustomFeeds(updated)
                      setEditingFeed(null)
                    }
                  }}
                  disabled={!editingFeed.name.trim() || editingFeed.slugs.size === 0}
                  style={{
                    flex: 1, padding: '4px 0', fontSize: 10, borderRadius: 4,
                    background: editingFeed.name.trim() && editingFeed.slugs.size > 0 ? AGORA_ACCENT : 'var(--bg-base)',
                    color: editingFeed.name.trim() && editingFeed.slugs.size > 0 ? 'white' : 'var(--text-muted)',
                    border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                  }}
                >
                  Save
                </button>
                <button
                  onClick={() => setEditingFeed(null)}
                  style={{
                    padding: '4px 8px', fontSize: 10, borderRadius: 4,
                    background: 'transparent', color: 'var(--text-muted)',
                    border: '1px solid var(--border)', cursor: 'pointer', fontFamily: 'inherit',
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div
              onClick={() => setEditingFeed({ name: '', slugs: new Set() })}
              style={{
                padding: '5px 10px', fontSize: 11, color: 'var(--text-muted)',
                cursor: 'pointer', borderRadius: 6, marginBottom: 8,
                transition: 'color 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.color = AGORA_ACCENT }}
              onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)' }}
            >
              + Create custom feed
            </div>
          )}

          {/* My Circles — Gathering org circles */}
          {gatheringCircles.length > 0 && (
            <div style={{ marginTop: 16, marginBottom: 16 }}>
              <div style={sidebarLabel}>Team Communities</div>
              {gatheringCircles.map(s => (
                <SidebarItem
                  key={s.slug}
                  label={s.title}
                  color={TYPE_COLORS[s.type] || '#8A7FB8'}
                  active={activeSpace === s.slug}
                  onClick={() => { setActiveSpace(s.slug); setActiveCustomFeed(null) }}
                />
              ))}
            </div>
          )}

          {/* Community Circles — topic circles with parent/child nesting */}
          <div style={{ marginBottom: 16 }}>
            <div style={sidebarLabel}>Topic Communities</div>
            {topLevelCommunity.map(s => {
              const children = childrenByParent[s.slug]
              const hasChildren = children && children.length > 0
              const isExpanded = expandedParents.has(s.slug)
              return (
                <div key={s.slug}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ flex: 1 }}>
                      <SidebarItem
                        label={s.title}
                        color={TYPE_COLORS[s.type] || '#8A7FB8'}
                        active={activeSpace === s.slug}
                        onClick={() => {
                          setActiveSpace(s.slug); setActiveCustomFeed(null)
                          if (hasChildren && !isExpanded) toggleParent(s.slug)
                        }}
                      />
                    </div>
                    {hasChildren && (
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleParent(s.slug) }}
                        style={{
                          background: isExpanded ? 'rgba(133,111,200,0.12)' : 'rgba(255,255,255,0.05)',
                          border: '1px solid ' + (isExpanded ? 'rgba(133,111,200,0.25)' : 'var(--border)'),
                          borderRadius: 4, cursor: 'pointer',
                          color: isExpanded ? AGORA_ACCENT : 'var(--text-muted)',
                          fontSize: 12, padding: '2px 5px', lineHeight: 1,
                          transition: 'all 0.2s',
                        }}
                        title={isExpanded ? 'Collapse' : `Show ${children.length} sub-communities`}
                      >
                        {isExpanded ? '▾' : '▸'}
                      </button>
                    )}
                  </div>
                  {hasChildren && isExpanded && (
                    <div style={{ paddingLeft: 12 }}>
                      {children.map(child => (
                        <SidebarItem
                          key={child.slug}
                          label={child.title}
                          color={TYPE_COLORS[child.type] || '#8A7FB8'}
                          active={activeSpace === child.slug}
                          onClick={() => { setActiveSpace(child.slug); setActiveCustomFeed(null) }}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
          <div style={{ height: 40 }} />
        </aside>

        {/* Main Feed */}
        <main style={{ minWidth: 0 }}>
          {/* Feed tabs */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
            {(['gravity', 'recent', 'top', 'rising', 'saved'] as FeedTab[]).map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                style={{
                  padding: '6px 14px',
                  borderRadius: 18,
                  fontSize: 12,
                  color: activeTab === tab ? 'var(--text-primary)' : 'var(--text-muted)',
                  background: activeTab === tab ? 'var(--bg-elevated, #1a1a1f)' : 'transparent',
                  border: `1px solid ${activeTab === tab ? 'var(--border-strong, #3a3a44)' : 'var(--border)'}`,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                {tab === 'gravity' ? 'Hot' : tab === 'recent' ? 'New' : tab === 'top' ? 'Top' : tab === 'rising' ? 'Rising' : `Saved (${savedIds.size})`}
              </button>
            ))}
          </div>

          {/* Top time window selector */}
          {activeTab === 'top' && (
            <div style={{ display: 'flex', gap: 4, marginBottom: 12, marginTop: -8 }}>
              {(['day', 'week', 'month', 'all'] as const).map(w => (
                <button
                  key={w}
                  onClick={() => setTopWindow(w)}
                  style={{
                    padding: '3px 10px', borderRadius: 12, fontSize: 11,
                    color: topWindow === w ? 'var(--text-primary)' : 'var(--text-muted)',
                    background: topWindow === w ? 'var(--bg-elevated)' : 'transparent',
                    border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                  }}
                >
                  {w === 'day' ? 'Today' : w === 'week' ? 'This Week' : w === 'month' ? 'This Month' : 'All Time'}
                </button>
              ))}
            </div>
          )}

          {/* Error state */}
          {feedError && (
            <div style={{
              padding: 20, background: 'var(--bg-surface)', border: '1px solid var(--border)',
              borderRadius: 12, color: 'var(--text-secondary)', fontSize: 13, marginBottom: 16,
            }}>
              Could not load posts ({feedError}).
            </div>
          )}

          {/* Loading */}
          {posts === null && !feedError && (
            <div style={{ padding: 20, color: 'var(--text-muted)', fontSize: 12, fontStyle: 'italic' }}>
              Loading posts…
            </div>
          )}

          {/* Empty */}
          {posts !== null && posts.length === 0 && !feedError && (
            <div style={{
              padding: '40px 16px', background: 'var(--bg-surface)', border: '1px solid var(--border)',
              borderRadius: 12, textAlign: 'center',
            }}>
              <div style={{ fontFamily: 'Playfair Display, Georgia, serif', fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
                {activeSpace ? 'No posts yet in this community' : 'The Agora is quiet'}
              </div>
              <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                Pick a community and be the first to post.
              </p>
            </div>
          )}

          {/* Posts */}
          {posts && posts.length > 0 && (() => {
            const now = Date.now()
            const timeMs: Record<string, number> = {
              today: 86400000,
              week: 604800000,
              month: 2592000000,
            }
            const filtered = posts.filter(post => {
              if (activeTab === 'saved' && !savedIds.has(post.id)) return false
              if (filterAuthor && post.author !== filterAuthor) return false
              if (filterTime && timeMs[filterTime] && now - new Date(post.created_at).getTime() > timeMs[filterTime]) return false
              if (filterHasReplies && !(post.reply_count && post.reply_count > 0)) return false
              if (filterHasAudio && !post.audio_url) return false
              if (filterSyntheses && !post.is_synthesis) return false
              return true
            })
            const sortNow = Date.now()
            const getScore = (p: Post) => {
              const votes = Object.values(p.reaction_counts || {}).reduce((s, n) => s + n, 0)
              return votes
            }
            const getAge = (p: Post) => (sortNow - new Date(p.created_at).getTime()) / 3600000

            if (activeTab === 'gravity') {
              filtered.sort((a, b) => {
                const scoreA = getScore(a) / Math.pow(getAge(a) + 2, 1.8)
                const scoreB = getScore(b) / Math.pow(getAge(b) + 2, 1.8)
                return scoreB - scoreA
              })
            } else if (activeTab === 'top') {
              const windowMs: Record<string, number> = { day: 86400000, week: 604800000, month: 2592000000, all: Infinity }
              const w = windowMs[topWindow] || Infinity
              if (w !== Infinity) {
                const cutoff = sortNow - w
                for (let i = filtered.length - 1; i >= 0; i--) {
                  if (new Date(filtered[i].created_at).getTime() < cutoff) filtered.splice(i, 1)
                }
              }
              filtered.sort((a, b) => getScore(b) - getScore(a))
            } else if (activeTab === 'rising') {
              filtered.sort((a, b) => {
                const ageA = getAge(a), ageB = getAge(b)
                const velA = ageA > 0 ? getScore(a) / ageA : getScore(a)
                const velB = ageB > 0 ? getScore(b) / ageB : getScore(b)
                return velB - velA
              })
            }

            filtered.sort((a, b) => {
              if (a.is_pinned && !b.is_pinned) return -1
              if (!a.is_pinned && b.is_pinned) return 1
              return 0
            })
            const hasFilters = filterAuthor || filterTime || filterHasReplies || filterHasAudio || filterSyntheses || activeTab === 'saved'
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {hasFilters && (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>
                    Showing {filtered.length} of {posts.length} posts
                  </div>
                )}
                {filtered.length === 0 && hasFilters && (
                  <div style={{
                    padding: '24px 16px', background: 'var(--bg-surface)', border: '1px solid var(--border)',
                    borderRadius: 12, textAlign: 'center', fontSize: 13, color: 'var(--text-muted)',
                  }}>
                    No posts match the current filters.
                  </div>
                )}
                {filtered.map(post => {
                  const space = (spaces || []).find(s => s.slug === post.space_slug)
                  const spaceColor = TYPE_COLORS[space?.type || ''] || undefined
                  const isSaved = savedIds.has(post.id)
                  return (
                    <div key={post.id} style={{ position: 'relative' }}>
                      <PostCard post={post} mockupHost={AGORA_MOCKUP_HOST} spaceColor={spaceColor} onAuthorClick={(author) => setFilterAuthor(author)} showPinButton={isSuper} spaces={(spaces || []).map(s => ({ slug: s.slug, title: s.title }))} />
                      <button
                        onClick={() => handleSavePost(post.id)}
                        title={isSaved ? 'Remove from Reading List' : 'Save to Reading List'}
                        style={{
                          position: 'absolute', top: 8, right: 8,
                          background: isSaved ? 'rgba(133,111,200,0.2)' : 'rgba(255,255,255,0.05)',
                          border: `1px solid ${isSaved ? AGORA_ACCENT + '66' : 'var(--border)'}`,
                          borderRadius: 6, padding: '3px 8px', fontSize: 11, cursor: 'pointer',
                          color: isSaved ? AGORA_ACCENT : 'var(--text-muted)',
                        }}
                      >
                        {isSaved ? '📌 Saved' : '🔖 Save'}
                      </button>
                    </div>
                  )
                })}
              </div>
            )
          })()}
        </main>

        {/* Right Sidebar */}
        <aside style={{
          position: 'sticky',
          top: 76,
          alignSelf: 'start',
          maxHeight: 'calc(100vh - 96px)',
          overflowY: 'auto',
        }}>
          {/* Feed Filters */}
          <SideCard title="Feed Filters">
            {/* By Author */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6, fontWeight: 600 }}>By Author</div>
              <select
                value={filterAuthor || ''}
                onChange={e => setFilterAuthor(e.target.value || null)}
                style={{
                  width: '100%', padding: '6px 8px', fontSize: 12, borderRadius: 6,
                  background: 'var(--bg-base)', color: 'var(--text-primary)',
                  border: '1px solid var(--border)', fontFamily: 'inherit',
                }}
              >
                <option value="">All authors</option>
                {posts && [...new Set(posts.map(p => p.author))].sort().map(a => (
                  <option key={a} value={a}>{a === 'dr-claude' ? 'Dr. Claude' : a.charAt(0).toUpperCase() + a.slice(1)}</option>
                ))}
              </select>
            </div>

            {/* By Time */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6, fontWeight: 600 }}>By Time</div>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {[
                  { key: null, label: 'All' },
                  { key: 'today', label: 'Today' },
                  { key: 'week', label: 'This Week' },
                  { key: 'month', label: 'This Month' },
                ].map(({ key, label }) => (
                  <button
                    key={label}
                    onClick={() => setFilterTime(key)}
                    style={{
                      padding: '4px 10px', borderRadius: 12, fontSize: 11,
                      background: filterTime === key ? AGORA_ACCENT : 'transparent',
                      color: filterTime === key ? 'white' : 'var(--text-muted)',
                      border: `1px solid ${filterTime === key ? AGORA_ACCENT : 'var(--border)'}`,
                      cursor: 'pointer', fontFamily: 'inherit',
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Toggle Filters */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <FilterToggle label="Has replies" active={filterHasReplies} onClick={() => setFilterHasReplies(!filterHasReplies)} />
              <FilterToggle label="Has audio" active={filterHasAudio} onClick={() => setFilterHasAudio(!filterHasAudio)} />
              <FilterToggle label="Syntheses only" active={filterSyntheses} onClick={() => setFilterSyntheses(!filterSyntheses)} />
            </div>

            {/* Clear */}
            {(filterAuthor || filterTime || filterHasReplies || filterHasAudio || filterSyntheses) && (
              <button
                onClick={() => { setFilterAuthor(null); setFilterTime(null); setFilterHasReplies(false); setFilterHasAudio(false); setFilterSyntheses(false) }}
                style={{
                  marginTop: 10, width: '100%', padding: '6px 0', fontSize: 11,
                  background: 'transparent', color: 'var(--text-muted)',
                  border: '1px solid var(--border)', borderRadius: 6,
                  cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                Clear all filters
              </button>
            )}
          </SideCard>

          {/* Etiquette */}
          <SideCard title="Community Etiquette">
            {[
              ['Post what is true.', 'Even if it\'s unfinished.'],
              ['Comment generously.', 'Presence counts; silence is fine, drive-bys aren\'t.'],
              ['Credit what moved you.', 'Name the thought, the person, the post.'],
              ['Weave, don\'t just reply.', 'Connect threads when you can see them.'],
              ['Silence is allowed.', 'Not every post needs an answer.'],
            ].map(([title, body], i) => (
              <div key={i} style={{
                padding: '8px 0',
                borderBottom: i < 4 ? '1px solid var(--border)' : 'none',
                fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5,
              }}>
                <strong style={{ color: 'var(--text-primary)', fontWeight: 600, display: 'block', marginBottom: 2 }}>
                  {title}
                </strong>
                {body}
              </div>
            ))}
          </SideCard>
        </aside>
      </div>
    </div>
  )
}

const sidebarLabel: React.CSSProperties = {
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: '1.2px',
  color: 'var(--text-muted)',
  marginBottom: 8,
  paddingLeft: 8,
}

function SidebarItem({ label, color, active, onClick }: {
  label: string
  color: string
  active: boolean
  onClick: () => void
}) {
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '8px 10px', borderRadius: 8, fontSize: 13,
        color: active ? 'var(--text-primary)' : 'var(--text-muted)',
        background: active ? 'var(--bg-elevated, #1a1a1f)' : 'transparent',
        cursor: 'pointer',
        transition: 'background 0.15s, color 0.15s',
        borderLeft: `3px solid ${active ? color : 'transparent'}`,
      }}
    >
      <span style={{
        width: 20, height: 20, borderRadius: 6, flexShrink: 0,
        background: color,
        opacity: active ? 1 : 0.7,
        transition: 'opacity 0.15s',
      }} />
      <span style={{ fontWeight: active ? 600 : 400 }}>{label}</span>
    </div>
  )
}

function SideCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{
      background: 'var(--bg-surface)',
      border: '1px solid var(--border)',
      borderRadius: 12,
      padding: 16,
      marginBottom: 16,
    }}>
      <h3 style={{
        fontSize: 13, textTransform: 'uppercase', letterSpacing: '1px',
        color: 'var(--text-muted)', marginBottom: 12,
      }}>
        {title}
      </h3>
      {children}
    </div>
  )
}

function FilterToggle({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '6px 0', cursor: 'pointer', fontSize: 12,
        color: active ? 'var(--text-primary)' : 'var(--text-muted)',
      }}
    >
      <span style={{
        width: 16, height: 16, borderRadius: 4, flexShrink: 0,
        border: active ? `2px solid ${AGORA_ACCENT}` : '2px solid var(--border)',
        background: active ? AGORA_ACCENT : 'transparent',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 10, color: 'white',
      }}>
        {active ? '✓' : ''}
      </span>
      <span>{label}</span>
    </div>
  )
}

